import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  Card,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  PaletteOutlined as PaletteOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopyRounded as ContentCopyRoundedIcon,
  EditSquare as EditSquareOutlinedIcon,
  FolderCopyOutlined as FolderCopyOutlinedIcon,
  LaptopWindowsRounded as LaptopWindowsRoundedIcon,
  LocalPrintshopOutlined as LocalPrintshopOutlinedIcon,
  ContrastOutlined as ContrastOutlinedIcon,
  DoneRounded as DoneRoundedIcon,
  CloseRounded as CloseRoundedIcon,
} from '@mui/icons-material';
import { toast } from 'utils/toast';
import { ContrastCheckerModal } from './ContrastCheckerModal';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { useNavigate } from 'react-router-dom';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { useGlobalConfig } from 'context/GlobalContext';
import { ColorPaletteAPI, ColorPaletteApiResponse } from 'apis/colorPaletteAPI';

// Component-level interfaces
interface ColorItem {
  name: string;
  hex: string;
  usage: number;
  contrast: number;
  contrastPass: boolean;
  shade?: string;
  paletteId?: string;
  colorMappings?: Record<string, string[]>;
}

interface ColorPalette {
  id: string;
  title: string;
  description: string;
  colors: ColorItem[];
}

// Transform API response to component structure
const transformApiResponse = (apiData: ColorPaletteApiResponse): ColorPalette[] => {
  return apiData.categories.map((category) => ({
    id: category.id,
    title: `${category.name} - ${category.description}`,
    description: category.description,
    colors: category.colors.map((color) => ({
      name: color.displayName,
      hex: color.hexValue,
      usage: color.usageCount,
      contrast: color.contrastRatio,
      contrastPass: color.contrastPass,
      shade: color.shade,
      paletteId: category.id,
      colorMappings: color.colorMappings,
    })),
  }));
};

// Helper to format color mappings as "configType.colorKey"
const formatColorMappings = (mappings?: Record<string, string[]>): string[] => {
  if (!mappings) return [];
  const formatted: string[] = [];
  Object.entries(mappings).forEach(([configType, colorKeys]) => {
    colorKeys.forEach((colorKey) => {
      formatted.push(`${configType}.${colorKey}`);
    });
  });
  return formatted;
};

const COLOR_PALETTE_SETTINGS_KEY = 'colorPaletteSettings';

export const ColorPalettePage: React.FC = () => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const navigate = useNavigate();
  const [projectSelection, setProjectSelection] = useState<'project' | 'standard'>('project');
  const [viewMode, setViewMode] = useState<'digital' | 'paper'>('digital');
  const [expandedPalettes, setExpandedPalettes] = useState<string[]>(['primary']);
  const [contrastCheckerOpen, setContrastCheckerOpen] = useState(false);
  
  const [colorPalettes, setColorPalettes] = useState<ColorPalette[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMappings, setShowMappings] = useState(false);
  
  const isBidgelyStandard = projectSelection === 'standard';

  // Expiry duration for colorPaletteSettings: 1 hour
  const SETTINGS_EXPIRY_MS = 60 * 60 * 1000; 

  const getCurrentProjectId = useCallback(() => {
    return projectSelection === 'project' ? (selectedPilotId || '') : '';
  }, [projectSelection, selectedPilotId]);

  const getCurrentChannel = useCallback((): 'DIGITAL' | 'PAPER' => {
    return viewMode === 'digital' ? 'DIGITAL' : 'PAPER';
  }, [viewMode]);

  const getColorPaletteSettings = useCallback(() => {
    try {
      const settingsStr = sessionStorage.getItem(COLOR_PALETTE_SETTINGS_KEY);
      if (!settingsStr) {
        return {};
      }
      const settings = JSON.parse(settingsStr);
      
      const now = Date.now();
      let hasExpired = false;
      Object.keys(settings).forEach((key) => {
        const entry = settings[key];
        const isExpired = entry?.expiryTs 
          ? now > entry.expiryTs 
          : (entry?.timestamp && (now - entry.timestamp) > SETTINGS_EXPIRY_MS);
        
        if (isExpired) {
          delete settings[key];
          hasExpired = true;
        }
      });
      
      if (hasExpired) {
        if (Object.keys(settings).length === 0) {
          sessionStorage.removeItem(COLOR_PALETTE_SETTINGS_KEY);
        } else {
          sessionStorage.setItem(COLOR_PALETTE_SETTINGS_KEY, JSON.stringify(settings));
        }
      }
      
      return settings;
    } catch {
      return {};
    }
  }, [SETTINGS_EXPIRY_MS]);

  const getSettingsKey = useCallback((projectId: string, channel: 'DIGITAL' | 'PAPER') => {
    return `${projectId || ''}_${channel}`;
  }, []);

  const getBackgroundColor = useCallback((): string => {
    try {
      const projectId = getCurrentProjectId();
      const channel = getCurrentChannel();
      const settings = getColorPaletteSettings();
      const key = getSettingsKey(projectId, channel);
      return settings[key]?.backgroundHEX || '#FFFFFF';
    } catch {
      return '#FFFFFF';
    }
  }, [getCurrentProjectId, getCurrentChannel, getColorPaletteSettings, getSettingsKey]);

  const fetchColorPalette = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const projectId = getCurrentProjectId();
      const channel = getCurrentChannel();
      const backgroundHEX = getBackgroundColor();
      
      const response = await ColorPaletteAPI.getColorPalette(rc, {
        channel,
        backgroundHEX,
        projectId: projectId || undefined,
      });
      
      if (!response.success || response.error) {
        console.error('[ColorPalettePage] API error:', response.error);
        setError(response.error || 'Failed to load color palette');
        toast.error(response.error || 'Failed to load color palette');
        return;
      }
      
      if (response.data) {
        const transformedData = transformApiResponse(response.data);
        setColorPalettes(transformedData);
        
        // Expand first palette with colors by default
        const firstPaletteWithColors = transformedData.find(p => p.colors.length > 0);
        if (firstPaletteWithColors) {
          setExpandedPalettes([firstPaletteWithColors.id]);
        }
      }
    } catch (err: any) {
      console.error('[ColorPalettePage] Failed to fetch color palette:', err);
      setError('Failed to load color palette');
      toast.error('Failed to load color palette');
    } finally {
      setLoading(false);
    }
  }, [getCurrentProjectId, getCurrentChannel, getBackgroundColor, rc]);

  useEffect(() => {
    fetchColorPalette();
  }, [fetchColorPalette]);

  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: 'digital' | 'paper' | null,
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
      // API will be called automatically via useEffect when viewMode changes
    }
  };

  const handleProjectSelectionChange = (value: 'project' | 'standard') => {
    setProjectSelection(value);
    // API will be called automatically via useEffect when projectSelection changes
  };

  const handlePaletteToggle = (paletteId: string) => {
    setExpandedPalettes((prev) =>
      prev.includes(paletteId)
        ? prev.filter((id) => id !== paletteId)
        : [...prev, paletteId],
    );
  };

  const handleCopyColor = (hex: string) => {
    navigator.clipboard.writeText(hex);
    toast.success(`Copied ${hex} to clipboard`);
  };

  const handleEditColor = (color: ColorItem) => {
    if (!color.paletteId || !color.shade) {
      console.error('Missing paletteId or shade for color:', color);
      return;
    }
    const standardColorId = `${color.paletteId}_${color.shade}`;
    const routePath = ROUTE_TREE.CONTENT_MANAGEMENT.EDIT_COLOR_PALETTE.path.replace(':standardColorId', standardColorId);
    const isReadOnly = projectSelection === 'standard';
    const params = new URLSearchParams();
    params.set('hex', color.hex);
    if (isReadOnly) {
      params.set('readonly', 'true');
    }
    navigate(`${routePath}?${params.toString()}`);
  };

  const handleColorSwatchClick = (color: ColorItem) => {
    if (isBidgelyStandard) return;
    handleEditColor(color);
  };

  const handleOpenContrastChecker = () => {
    setContrastCheckerOpen(true);
  };

  const handleCloseContrastChecker = () => {
    setContrastCheckerOpen(false);
  };

  const handleSaveContrastBackground = async (backgroundColor: string) => {
    try {
      const projectId = getCurrentProjectId();
      const channel = getCurrentChannel();
      
      // Save to sessionStorage with expiryTs (1 hour from now)
      const settings = getColorPaletteSettings();
      const key = getSettingsKey(projectId, channel);
      const now = Date.now();
      settings[key] = {
        projectId: projectId || '',
        channel: channel,
        backgroundHEX: backgroundColor.toUpperCase(),
        timestamp: now,
        expiryTs: now + SETTINGS_EXPIRY_MS
      };
      sessionStorage.setItem(COLOR_PALETTE_SETTINGS_KEY, JSON.stringify(settings));
      
      toast.success(`Contrast background saved: ${backgroundColor}`);
      
      // Re-fetch color palette with new background to recalculate contrast ratios
      await fetchColorPalette();
      
    } catch (err) {
      console.error('Error saving contrast background:', err);
      toast.error('Failed to save contrast background');
    }
  };

  const handleExpandCollapseAll = () => {
    if (expandedPalettes.length === colorPalettes.length && colorPalettes.length > 0) {
      setExpandedPalettes([]);
    } else {
      setExpandedPalettes(colorPalettes.map(p => p.id));
    }
  };

  const allExpanded = colorPalettes.length > 0 && expandedPalettes.length === colorPalettes.length;

  return (
    <Box sx={{ 
      bgcolor: theme.palette.background.default, 
      minHeight: '100vh',
      py: 4,
    }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3 }}>
        {/* Header Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PaletteOutlinedIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ 
                  fontWeight: 800,
                  color: theme.palette.text.primary,
                }}
              >
                Color Palette
              </Typography>
            </Box>
            {/* Toggle Button Group for Digital/Paper */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              aria-label="view mode"
              size="medium"
            >
              <ToggleButton
                value="digital"
                aria-label="digital"
                sx={{
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  bgcolor: viewMode === 'digital' 
                    ? (theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main)
                    : theme.palette.mode === 'dark' 
                      ? theme.palette.background.paper 
                      : theme.palette.grey[100],
                  color: viewMode === 'digital' 
                    ? theme.palette.primary.contrastText 
                    : theme.palette.text.secondary,
                  borderColor: theme.palette.divider,
                  '&.Mui-selected': {
                    bgcolor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    borderColor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                    },
                  },
                }}
              >
                <LaptopWindowsRoundedIcon sx={{ fontSize: 18 }} />
                Digital
              </ToggleButton>
              <ToggleButton
                value="paper"
                aria-label="paper"
                sx={{
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  bgcolor: viewMode === 'paper' 
                    ? (theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main)
                    : theme.palette.mode === 'dark' 
                      ? theme.palette.background.paper 
                      : theme.palette.grey[100],
                  color: viewMode === 'paper' 
                    ? theme.palette.primary.contrastText 
                    : theme.palette.text.secondary,
                  borderColor: theme.palette.divider,
                  '&.Mui-selected': {
                    bgcolor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    borderColor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                    },
                  },
                }}
              >
                <LocalPrintshopOutlinedIcon sx={{ fontSize: 18 }} />
                Paper
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Main Content Card */}
        <Card sx={{ 
          p: 0, 
          borderRadius: 3, 
          boxShadow: 1,
          bgcolor: theme.palette.background.paper,
        }}>
          {/* Subtitle */}
          <Box sx={{ 
            px: 3,
            pt: 3,
            pb: 2,
            mb: 2,
            borderBottom: '1px solid',
            borderColor: theme.palette.divider,
          }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: theme.palette.text.secondary,
                textAlign: 'left',
              }}
            >
              {viewMode === 'digital' 
                ? "Manage the project's color palette and themes for digital products like web and email."
                : "Manage the project's color palette for paper products like HERs. Colors may need adjustment for print."}
            </Typography>
          </Box>

          {/* Controls and Color Palette Sections */}
          <Box sx={{ px: 3, py: 2, pb: 4 }}>
            {/* Controls Section */}
            <Box sx={{ 
              mb: 2,
              p: 2,
              bgcolor: theme.palette.mode === 'dark' 
                ? theme.palette.background.paper 
                : theme.palette.action.hover,
              borderRadius: 2,
              boxShadow: theme.palette.mode === 'dark' ? 0 : 1,
              border: '1px solid',
              borderColor: theme.palette.divider,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <Select
                    value={projectSelection}
                    onChange={(e) => handleProjectSelectionChange(e.target.value as 'project' | 'standard')}
                    displayEmpty
                    sx={{
                      bgcolor: theme.palette.mode === 'dark' 
                        ? theme.palette.background.paper 
                        : theme.palette.grey[50],
                      borderRadius: 1,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.divider,
                        borderWidth: '1px',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#008080',
                        borderWidth: '2px',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#008080',
                        borderWidth: '2px',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          '& .MuiMenuItem-root': {
                            '&:hover': {
                              bgcolor: '#008080',
                              color: theme.palette.primary.contrastText,
                            },
                            '&.Mui-selected': {
                              bgcolor: '#008080',
                              color: theme.palette.primary.contrastText,
                              '&:hover': {
                                bgcolor: '#008080',
                              },
                            },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="project">Project Colors</MenuItem>
                    <MenuItem value="standard">Bidgely Standard</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showMappings}
                      onChange={(e) => setShowMappings(e.target.checked)}
                      size="small"
                      sx={{
                        width: 38,
                        height: 22,
                        padding: 0,
                        '& .MuiSwitch-switchBase': {
                          padding: 0,
                          margin: '2px',
                          transitionDuration: '300ms',
                          '&.Mui-checked': {
                            transform: 'translateX(16px)',
                            color: '#fff',
                            '& + .MuiSwitch-track': {
                              backgroundColor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                              opacity: 1,
                              border: 0,
                            },
                            '& .MuiSwitch-thumb': {
                              color: '#fff',
                              border: 'none',
                            },
                          },
                          '&.Mui-focusVisible .MuiSwitch-thumb': {
                            color: theme.palette.primary.main,
                            border: '6px solid #fff',
                          },
                        },
                        '& .MuiSwitch-thumb': {
                          boxSizing: 'border-box',
                          width: 18,
                          height: 18,
                          color: '#fff',
                        },
                        '& .MuiSwitch-track': {
                          borderRadius: 22 / 2,
                          backgroundColor: showMappings
                            ? (theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main)
                            : theme.palette.mode === 'dark' ? '#4a5568' : '#E9E9EA',
                          opacity: 1,
                          transition: theme.transitions.create(['background-color'], {
                            duration: 300,
                          }),
                        },
                      }}
                    />
                  }
                  label="Show Mappings"
                  sx={{ ml: 0 }}
                />
                <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ContrastOutlinedIcon />}
                    size="small"
                    onClick={handleOpenContrastChecker}
                    sx={{
                      textTransform: 'none',
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      bgcolor: theme.palette.mode === 'dark' 
                        ? theme.palette.background.paper 
                        : theme.palette.grey[50],
                      borderRadius: 1,
                      '&:hover': {
                        borderColor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                        bgcolor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                      },
                    }}
                  >
                    Contrast Checker
                  </Button>
                  <Tooltip title={allExpanded ? "Collapse All" : "Expand All"}>
                    <IconButton
                      size="small"
                      onClick={handleExpandCollapseAll}
                      disabled={colorPalettes.length === 0}
                      sx={{
                        width: 36,
                        height: 36,
                        border: '1px solid',
                        borderColor: theme.palette.divider,
                        borderRadius: 1,
                        bgcolor: theme.palette.mode === 'dark' 
                          ? theme.palette.background.paper 
                          : theme.palette.grey[50],
                        '&:hover': {
                          borderColor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                          bgcolor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                        },
                      }}
                    >
                      <FolderCopyOutlinedIcon 
                        fontSize="small" 
                        sx={{ 
                          color: theme.palette.mode === 'dark' 
                            ? theme.palette.text.primary 
                            : 'inherit' 
                        }} 
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            {/* Color Palette Sections */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
                  <Button variant="outlined" onClick={fetchColorPalette}>Retry</Button>
                </Box>
              ) : colorPalettes.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="text.secondary">No color palettes found</Typography>
                </Box>
              ) : (
                colorPalettes.map((palette) => {
                  const isExpanded = expandedPalettes.includes(palette.id);
                  const hasColors = palette.colors.length > 0;

                  return (
                    <Accordion
                      key={palette.id}
                      expanded={isExpanded}
                      onChange={() => handlePaletteToggle(palette.id)}
                      sx={{
                        boxShadow: 1,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: theme.palette.divider,
                        bgcolor: theme.palette.mode === 'dark' 
                          ? theme.palette.background.paper 
                          : theme.palette.action.hover,
                        '&:before': { display: 'none' },
                        '& .MuiAccordionSummary-root': {
                          bgcolor: theme.palette.mode === 'dark' 
                            ? theme.palette.background.paper 
                            : theme.palette.action.hover,
                          minHeight: 56,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: theme.palette.divider,
                          '&.Mui-expanded': {
                            minHeight: 56,
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                            borderBottom: 'none',
                          },
                        },
                        '& .MuiAccordionDetails-root': {
                          p: 0,
                          bgcolor: theme.palette.background.paper,
                          borderBottomLeftRadius: 2,
                          borderBottomRightRadius: 2,
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          px: 3,
                          maxWidth: '100%',
                          '& .MuiAccordionSummary-content': {
                            my: 1.5,
                            maxWidth: 'calc(100% - 40px)',
                            alignItems: 'center',
                          },
                          '& .MuiAccordionSummary-expandIconWrapper': {
                            color: theme.palette.mode === 'dark' 
                              ? theme.palette.text.primary 
                              : theme.palette.text.secondary,
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          {palette.title.includes(' - ') ? (
                            <>
                              <Typography 
                                variant="body1" 
                                component="span"
                                sx={{ 
                                  fontWeight: 600, 
                                  color: theme.palette.mode === 'dark' 
                                    ? theme.palette.text.primary 
                                    : '#6c7f93',
                                }}
                              >
                                {palette.title.split(' - ')[0]}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                component="span"
                                sx={{ 
                                  fontWeight: 400,
                                  fontSize: '0.75rem',
                                  color: theme.palette.text.secondary,
                                  ml: 0.5,
                                }}
                              >
                                - {palette.title.split(' - ')[1]}
                              </Typography>
                            </>
                          ) : (
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: 600, 
                                color: theme.palette.mode === 'dark' 
                                  ? theme.palette.text.primary 
                                  : '#6c7f93',
                              }}
                            >
                              {palette.title}
                            </Typography>
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {hasColors ? (
                          <TableContainer component={Paper} elevation={0} sx={{ borderTop: '1px solid', borderColor: theme.palette.divider }}>
                            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                              <TableHead>
                                <TableRow sx={{ 
                                  bgcolor: theme.palette.mode === 'dark'
                                    ? theme.palette.action.hover
                                    : theme.palette.grey[100]
                                }}>
                                  <TableCell sx={{ fontWeight: 600, py: 1.5, color: theme.palette.text.primary, width: showMappings ? '18%' : '25%' }}>Name/Shade</TableCell>
                                  <TableCell sx={{ fontWeight: 600, py: 1.5, color: theme.palette.text.primary, width: showMappings ? '22%' : '30%' }}>Color</TableCell>
                                  {!isBidgelyStandard && (
                                    <TableCell sx={{ fontWeight: 600, py: 1.5, color: theme.palette.text.primary, width: showMappings ? '10%' : '15%' }}>Usage</TableCell>
                                  )}
                                  <TableCell sx={{ fontWeight: 600, py: 1.5, color: theme.palette.text.primary, width: showMappings ? '10%' : '15%' }}>Contrast</TableCell>
                                  {showMappings && (
                                    <TableCell sx={{ fontWeight: 600, py: 1.5, color: theme.palette.text.primary, width: '25%' }}>Color Mappings</TableCell>
                                  )}
                                  {!isBidgelyStandard && (
                                    <TableCell sx={{ fontWeight: 600, py: 1.5, color: theme.palette.text.primary, width: showMappings ? '15%' : '15%' }}>Actions</TableCell>
                                  )}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {palette.colors.map((color) => (
                                  <TableRow
                                    key={color.name}
                                    sx={{
                                      '&:hover': {
                                        bgcolor: theme.palette.action.hover,
                                      },
                                    }}
                                  >
                                    <TableCell sx={{ py: 1.5, color: theme.palette.text.primary }}>{color.name}</TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Tooltip title={isBidgelyStandard ? color.hex : "Click to edit color"}>
                                          <Box
                                            onClick={() => handleColorSwatchClick(color)}
                                            sx={{
                                              width: 56,
                                              height: 32,
                                              borderRadius: 1,
                                              bgcolor: color.hex,
                                              border: '1px solid',
                                              borderColor: theme.palette.divider,
                                              flexShrink: 0,
                                              cursor: isBidgelyStandard ? 'default' : 'pointer',
                                              '&:hover': isBidgelyStandard ? {} : {
                                                borderWidth: '2px',
                                                borderColor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                                              },
                                            }}
                                          />
                                        </Tooltip>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: theme.palette.text.primary }}>
                                          {color.hex}
                                        </Typography>
                                        <Tooltip title="Copy hex code">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleCopyColor(color.hex)}
                                            sx={{ 
                                              p: 0.5,
                                              '& .MuiSvgIcon-root': {
                                                color: theme.palette.mode === 'dark' 
                                                  ? theme.palette.text.primary 
                                                  : 'inherit',
                                              },
                                            }}
                                          >
                                            <ContentCopyRoundedIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </TableCell>
                                    {!isBidgelyStandard && (
                                      <TableCell sx={{ py: 1.5 }}>
                                        <Box
                                          sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            bgcolor: theme.palette.mode === 'dark' 
                                              ? '#334155'
                                              : theme.palette.grey[100],
                                            color: theme.palette.mode === 'dark'
                                              ? '#fff'
                                              : theme.palette.text.primary,
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                          }}
                                        >
                                          {color.usage}
                                        </Box>
                                      </TableCell>
                                    )}
                                    <TableCell sx={{ py: 1.5 }}>
                                      <Chip
                                        label={color.contrast.toFixed(2)}
                                        size="small"
                                        icon={color.contrast > 4.5 ? (
                                          <DoneRoundedIcon sx={{ fontSize: 16 }} />
                                        ) : (
                                          <CloseRoundedIcon sx={{ fontSize: 16 }} />
                                        )}
                                        sx={{
                                          bgcolor: color.contrast > 4.5 
                                            ? '#E8F5E9' 
                                            : '#FFE5E5',
                                          color: color.contrast > 4.5 
                                            ? '#2E7D32' 
                                            : '#8B0000',
                                          border: color.contrast > 4.5 
                                            ? '1px solid #A5D6A7' 
                                            : '1px solid #FFB3B3',
                                          borderRadius: '16px',
                                          height: 24,
                                          fontSize: '0.75rem',
                                          fontWeight: 500,
                                          '& .MuiChip-label': {
                                            px: 1.5,
                                          },
                                          '& .MuiChip-icon': {
                                            color: color.contrast > 4.5 
                                              ? '#2E7D32' 
                                              : '#8B0000',
                                          },
                                        }}
                                      />
                                    </TableCell>
                                    {showMappings && (
                                      <TableCell sx={{ py: 1.5 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                          {formatColorMappings(color.colorMappings).length > 0 ? (
                                            formatColorMappings(color.colorMappings).map((mapping, idx) => (
                                              <Typography 
                                                key={idx}
                                                variant="body2" 
                                                sx={{ 
                                                  fontFamily: 'monospace', 
                                                  fontSize: '0.75rem',
                                                  color: theme.palette.text.secondary,
                                                  wordBreak: 'break-all',
                                                }}
                                              >
                                                {mapping}
                                              </Typography>
                                            ))
                                          ) : (
                                            <Typography 
                                              variant="body2" 
                                              sx={{ 
                                                fontSize: '0.75rem',
                                                color: theme.palette.mode === 'dark' 
                                                  ? theme.palette.text.secondary 
                                                  : theme.palette.text.disabled,
                                                fontStyle: 'italic',
                                              }}
                                            >
                                              No mappings Available
                                            </Typography>
                                          )}
                                        </Box>
                                      </TableCell>
                                    )}
                                    {!isBidgelyStandard && (
                                      <TableCell sx={{ py: 1.5 }}>
                                        <Tooltip title="Edit Color">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleEditColor(color)}
                                            sx={{ 
                                              p: 0.5,
                                              '& .MuiSvgIcon-root': {
                                                color: theme.palette.mode === 'dark' 
                                                  ? theme.palette.text.primary 
                                                  : 'inherit',
                                              },
                                            }}
                                          >
                                            <EditSquareOutlinedIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </TableCell>
                                    )}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>No colors defined</Typography>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              )}
            </Box>
          </Box>
        </Card>
      </Box>

      {/* Contrast Checker Modal */}
      <ContrastCheckerModal
        open={contrastCheckerOpen}
        onClose={handleCloseContrastChecker}
        onSave={handleSaveContrastBackground}
        initialBackgroundColor={getBackgroundColor()}
      />
    </Box>
  );
};

export default ColorPalettePage;
